import type {
  AllListingsFilterRegionElement,
  AllListingsFilters,
} from '@b-stock/search-api-client'

type InternalNode = {
  nodeType: 'INTERNAL' | 'LEAF'
  key: string
  displayName: string
  position: string[]
  children: InternalNode[]
  count: number
}

const buildRegionTree = (
  regions: AllListingsFilters['region'] = [],
  excludeZero = false
): InternalNode[] => {
  const root: { [key: string]: InternalNode } = {}

  if (excludeZero) {
    regions = regions.filter(({ count }) => (count ?? 0) > 0)
  }

  regions.forEach((region) => {
    const { count = 0, position = [], value = '' } = region
    const [parentName, childName, grandchildName] = position

    // Invariant: parentName, childName, grandchildName exist when position.length === 3
    if (parentName === 'United States' && position.length === 3) {
      if (!(parentName in root)) {
        root[parentName] = {
          nodeType: 'INTERNAL',
          key: parentName,
          displayName: parentName,
          children: [],
          position,
          count,
        }
      }

      // parentName guaranteed to exist in root due to check above
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      let childNode = root[parentName]!.children.find(
        (node) => node.displayName === childName
      )
      if (!childNode) {
        childNode = {
          nodeType: 'INTERNAL',
          // childName guaranteed to exist when position.length === 3
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          key: childName!,
          // childName guaranteed to exist when position.length === 3
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          displayName: childName!,
          children: [],
          position,
          count,
        }
        // parentName guaranteed to exist in root due to check above
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        root[parentName]!.children.push(childNode)
      }

      const grandChildNode: InternalNode = {
        nodeType: 'LEAF',
        key: value,
        // grandchildName guaranteed to exist when position.length === 3
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        displayName: grandchildName!,
        children: [],
        position,
        count,
      }
      childNode.children.push(grandChildNode)
    } else if (position.length === 2) {
      const [parent, child] = position

      // Invariant: parent and child exist when position.length === 2
      // parent guaranteed to exist when position.length === 2
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      if (!(parent! in root)) {
        // parent guaranteed to exist when position.length === 2
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        root[parent!] = {
          nodeType: 'INTERNAL',
          // parent guaranteed to exist when position.length === 2
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          key: parent!,
          // parent guaranteed to exist when position.length === 2
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          displayName: parent!,
          children: [],
          position,
          count,
        }
      }

      const childNode: InternalNode = {
        nodeType: 'LEAF',
        key: value,
        // child guaranteed to exist when position.length === 2
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        displayName: child!,
        children: [],
        position,
        count,
      }
      // Parent node was just created or already exists
      // parent guaranteed to exist when position.length === 2 (both assertions)
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      root[parent!]!.children.push(childNode)
    }
  })

  const sortTree = (nodes: InternalNode[]): InternalNode[] => {
    return nodes
      .map((node) => ({
        ...node,
        children: sortTree(node.children),
      }))
      .sort((a, b) => {
        if (a.displayName === 'United States') return -1 // United States first
        if (b.displayName === 'United States') return 1 // All other regions
        return a.displayName.localeCompare(b.displayName)
      })
  }

  return sortTree(Object.values(root))
}

const getCountryKey = (key: string) => key.replace(/^[^/]+/, '')
const getChildrenKeys = (nodes: InternalNode[]) => nodes.map((c) => c.key)
const getGrandchildrenKeys = (nodes: InternalNode[]) =>
  nodes.flatMap((c) => getChildrenKeys(c.children))

type ChildKeys = { [key: string]: string[] }

const getChildKeys = (nodes: InternalNode[]) =>
  nodes.reduce<ChildKeys>((acc, child) => {
    acc[child.key] = getChildrenKeys(child.children)
    return acc
  }, {})

const hasEveryChild = (keys: string[], nodes: Set<string> | string[]) =>
  keys.every((k) => (Array.isArray(nodes) ? nodes.includes(k) : nodes.has(k)))

// takes an array of region query keys and converts them to display names
// ["CA/US", "/IT"] => ["California", "Italy"]
// [...all European countries...] => ["Europe"]
// [...all Midwest states...] => ["Midwest"]
// ["/CA", "/US"] => ["Canada", "United States"]
const formatKeysToNames = (
  regions: string[],
  allRegions: AllListingsFilterRegionElement[] | undefined
) => {
  const regionTree = buildRegionTree(allRegions)
  const displayRegionTree = buildRegionTree(allRegions, true)

  // reduce the keys to just the ones we want display names for
  regionTree.forEach((node) => {
    const displayChildren = displayRegionTree.find(
      ({ key }) => key === node.key
    )?.children

    if (!displayChildren) return

    const isGrandparent =
      node.position.length === 3 && node.key === node.position[0]

    const allGrandChildrenKeys = getGrandchildrenKeys(node.children)
    const displayGrandChildrenKeys = getGrandchildrenKeys(displayChildren)
    const allChildrenKeys = getChildrenKeys(node.children)
    const displayChildrenKeys = getChildrenKeys(displayChildren)

    // utility to see if every region belonging to a parent has been selected
    const hasEveryRegion = (keys: string[]) =>
      keys.every((k) => regions.includes(k))

    // utility to remove children keys and add the parent key
    // node.key is a display name or query key ("United States" or "/IT")
    const filterKeys = (keys: string[], simplifiedKey = node.key) => {
      const filteredRegions = regions.filter((region) => !keys.includes(region))
      return [...filteredRegions, simplifiedKey]
    }

    // if all grandchildren are selected, distill down to just the grandparent region
    if (
      allGrandChildrenKeys.length > 0 &&
      hasEveryRegion(displayGrandChildrenKeys)
    ) {
      regions = filterKeys(allGrandChildrenKeys)
    }
    // if all direct regions are selected, distill down to just the direct region
    else if (isGrandparent) {
      const allDirectChildrenKeys = getChildKeys(node.children)
      const displayDirectChildrenKeys = getChildKeys(displayChildren)
      Object.entries(allDirectChildrenKeys).forEach(([key, childKeys]) => {
        const displayKeys = displayDirectChildrenKeys[key]
        if (displayKeys === undefined) return
        if (hasEveryRegion(displayKeys)) {
          regions = filterKeys(childKeys, key)
        }
      })
    }
    // if all countries within a region are selected, distill down to just the region
    else if (hasEveryRegion(displayChildrenKeys)) {
      regions = filterKeys(allChildrenKeys)
    }
  })

  // convert the remaining keys to display names
  regions = regions
    .map((region) => {
      // region can be a display name already like "United States" or a query key like "/IT"
      // position will return undefined in the former case, and a position array in the latter
      const position: string[] | undefined = allRegions?.find(
        ({ value }) => value === region
      )?.position

      if (position && position.length > 0) {
        // the display name should be the last item in the position
        // position.length > 0 guarantees last item exists
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        return position[position.length - 1]!
      }

      // if what we're left with is a query key because the supplied regions
      // includes a value (e.g. XX/US) that is not a valid allFilters.region value
      const parts = region.split('/')
      const isQueryKey = parts.every((part) => part === '' || part.length === 2)

      if (isQueryKey) {
        // if it matches anything we do have (e.g. "/US" matches "AL/US"), get the first one's display name
        const matchingPositionName = allRegions?.find(({ value }) =>
          value?.includes(region)
        )?.position?.[0]
        // if there a match, return it, else, return null and we'll filter those out
        return matchingPositionName ?? null
      }

      // if we get this far, we have a display name to return. It's still possible
      // we wind up with an invalid string with a leading slash (e.g. /Alberta) so strip that out
      return region.replace('/', '')
    })
    // filter out erroneous query keys
    .filter((region): region is string => region !== null)

  return regions
}

// takes an array of region display names and converts them to query keys
// ["California", "Italy"] => ["CA/US", "/IT"]
// ["Europe"] => [...all European countries...]
// ["Midwest"] => [...all Midwest states...]
// ["Canada", "United States"] => ["/CA", "/US"]
const formatNamesToKeys = (
  regions: string[],
  allRegions: AllListingsFilters['region']
) => {
  const regionTree = buildRegionTree(allRegions)

  const findNode = (
    region: string,
    nodes: InternalNode[]
  ): InternalNode | null => {
    for (const node of nodes) {
      if (node.key === region || node.displayName === region) {
        return node
      }

      if (node.children.length > 0) {
        const result = findNode(region, node.children)
        if (result) {
          return result
        }
      }
    }

    return null
  }

  // display name to query key
  const format = (region: string) => {
    const regionNode = findNode(region, regionTree)

    // if the region is already a query key ("AL/US") or the region is unknown
    if (!regionNode) {
      // must return a value with "/" else the query returns an error
      // this can result in sending something like "/foo", but the search
      // service handles this gracefully
      return [region.includes('/') ? region : `/${region}`]
    }

    if (regionNode.nodeType === 'LEAF') {
      return [regionNode.key]
    }

    // if all children end with the same country
    return regionNode.children.flatMap((child) => {
      // if this node has no children
      if (child.nodeType === 'LEAF') {
        const countryKey = getCountryKey(child.key)

        // if all the children are the in the same country ["ON/CA", "BC/CA"]
        if (
          child.position.length === 2 &&
          regionNode.children.every(({ key }) => key.endsWith(countryKey))
        ) {
          return countryKey
        }

        return [child.key]
      }

      // if this node has grandchildren ["United States", "West", "California"]
      // this can return many keys of the same value, we reduce them to one later
      return child.children.map((grandChild) => getCountryKey(grandChild.key))
    })
  }

  // switch the flatMap into a set for easy de-duplication, then switch back to array
  return Array.from(new Set(regions.flatMap(format)))
}

const regionFilterContribution = (
  value: string[],
  allRegions: AllListingsFilters['region']
) => {
  const formatted = formatKeysToNames(value, allRegions)
  return formatted.length
}

/**
 * Computes the minimal set of external/normalized keys that represents
 * "all display items selected". Mirrors the isInternal=false branch of
 * HierarchicalCategoricalFilter's processNodes when the full leaf set is
 * provided, so the result is consistent with what the filter's onChange
 * would produce when the user manually selects every item.
 */
const getSelectAllKeys = (
  displayNodes: InternalNode[],
  allNodes: InternalNode[]
): string[] => {
  const result = new Set<string>()

  displayNodes.forEach((node) => {
    const nodeChildren = getChildrenKeys(node.children)
    const allNode = allNodes.find(({ key }) => key === node.key)
    const allNodeChildren = allNode
      ? getChildrenKeys(allNode.children)
      : nodeChildren

    if (nodeChildren.length === 0) {
      result.add(node.key)
      return
    }

    const isParentOnly = nodeChildren.every((c) => c.includes('/'))

    if (isParentOnly) {
      // Non-null: nodeChildren.length > 0 verified above
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const parentKey = getCountryKey(nodeChildren[0]!)
      const isAllSameParent = nodeChildren.every((k) => k.includes(parentKey))

      if (isAllSameParent) {
        const hasDistinctParent = nodeChildren.some((c) => c !== parentKey)
        if (hasDistinctParent) {
          // e.g. Canada provinces → /CA
          result.add(parentKey)
        } else {
          // Single-leaf country (parent key === child key) → expand via allNodes
          allNodeChildren.forEach((k) => result.add(k))
        }
      } else {
        // Multi-country group like Europe → individual country keys
        allNodeChildren.forEach((k) => result.add(k))
      }
    } else {
      // 3-level node (United States: US → region → state)
      const nodeGrandchildren = getGrandchildrenKeys(node.children)
      if (nodeGrandchildren.length > 0) {
        // Non-null: nodeGrandchildren.length > 0 verified above
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        result.add(getCountryKey(nodeGrandchildren[0]!))
      }
    }
  })

  return [...result]
}

/**
 * Returns true if `itemValue` is directly in the set, OR if its parent
 * key (e.g. /CA from BC/CA) is in the set. This lets FiltersModal
 * correctly report "all selected" when the stored representation uses
 * normalized parent keys rather than raw leaf keys.
 */
const isRegionItemCoveredBySet = (
  itemValue: string,
  selectedSet: Set<string>
): boolean =>
  selectedSet.has(itemValue) || selectedSet.has(getCountryKey(itemValue))

export {
  type InternalNode,
  buildRegionTree,
  getCountryKey,
  getChildrenKeys,
  getGrandchildrenKeys,
  getChildKeys,
  hasEveryChild,
  formatKeysToNames,
  formatNamesToKeys,
  regionFilterContribution,
  getSelectAllKeys,
  isRegionItemCoveredBySet,
}
